import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, ...rest }, ref) => (
    <input
      ref={ref}
      className={cn(
        'w-full h-12 px-4 bg-paper border rounded-xs',
        'font-body text-base text-ink placeholder:text-muted',
        'transition-colors duration-150 ease-out-soft',
        'focus:outline-none focus:ring-0',
        error
          ? 'border-danger focus:border-danger'
          : 'border-line focus:border-ink',
        className,
      )}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'w-full px-4 py-3 bg-paper border rounded-xs',
        'font-body text-base text-ink placeholder:text-muted resize-none',
        'transition-colors duration-150 ease-out-soft',
        'focus:outline-none focus:ring-0',
        error ? 'border-danger focus:border-danger' : 'border-line focus:border-ink',
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

/**
 * Form field wrapper — label, hint, error message.
 * Use with Input/Textarea inside.
 */
export function Field({ label, htmlFor, hint, error, children }: FieldProps) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="block font-body text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p className="font-body text-sm text-danger">{error}</p>
      ) : hint ? (
        <p className="font-body text-sm text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
